import { getObjectUrl, normalizeCosKey } from './cos-client';

export const DEFAULT_SIGNED_URL_BATCH_SIZE = 100;

export type AttachSignedUrlsOptions<T> = {
  extractObjectKey: (item: T) => string;
  applySignedUrl: (item: T, ctx: { objectKey: string; signedUrl: string }) => T;
  batchSize?: number;
};

export async function getSignedUrlMap(
  objectKeys: readonly string[],
  options?: { batchSize?: number },
): Promise<Map<string, string>> {
  const batchSize = options?.batchSize ?? DEFAULT_SIGNED_URL_BATCH_SIZE;
  const uniqueKeys = [...new Set(
    objectKeys
      .map((key) => String(key ?? '').trim())
      .filter(Boolean),
  )];
  const signed = new Map<string, string>();

  for (let i = 0; i < uniqueKeys.length; i += batchSize) {
    const chunk = uniqueKeys.slice(i, i + batchSize);
    const part = await Promise.all(
      chunk.map(async (objectKey) => {
        try {
          const signedUrl = String(await getObjectUrl(objectKey) ?? '').trim();
          return signedUrl ? ([objectKey, signedUrl] as const) : null;
        } catch {
          return null;
        }
      }),
    );
    for (const entry of part) {
      if (entry) signed.set(entry[0], entry[1]);
    }
  }

  return signed;
}

export async function attachSignedUrls<T>(
  items: readonly T[],
  options: AttachSignedUrlsOptions<T>,
): Promise<T[]> {
  if (!items.length) return [];

  const entries = items.map((item) => ({
    item,
    objectKey: normalizeCosKey(String(options.extractObjectKey(item) ?? '').trim()),
  }));

  const signableKeys = entries
    .map((entry) => entry.objectKey)
    .filter(Boolean);
  const signedByKey = signableKeys.length
    ? await getSignedUrlMap(signableKeys, { batchSize: options.batchSize })
    : new Map<string, string>();

  return entries.map(({ item, objectKey }) => {
    if (!objectKey) return item;
    const signedUrl = String(signedByKey.get(objectKey) ?? '').trim();
    if (!signedUrl) return item;
    return options.applySignedUrl(item, { objectKey, signedUrl });
  });
}
